export const TRANSPORT_REQUEST_TYPES = [
  "new-transportation-house-to-warehouse",
  "new-transportation-warehouse-to-house",
  "new-transportation-house-to-house",
  "new-transportation-city-to-city",
  "new-transportation-international",
];

export function isTransportRequestType(requestType) {
  return TRANSPORT_REQUEST_TYPES.includes(String(requestType || "").trim());
}

export function normalizeCompanyTypes(value) {
  const toArray = (input) => {
    if (Array.isArray(input)) return input;
    if (input === null || input === undefined || input === false) return [];
    if (typeof input === "string") {
      const trimmed = input.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed;
        } catch (_) {
          // no-op
        }
      }
      return trimmed.split(/[,;|]/);
    }
    return [input];
  };

  return Array.from(
    new Set(toArray(value).map((item) => String(item ?? "").trim()).filter(Boolean))
  );
}

export function companyHasTransportType(company) {
  return normalizeCompanyTypes(company?.type).some(
    (item) =>
      String(item || "").toLowerCase() === "nakliye" ||
      String(item || "")
        .toLowerCase()
        .split(/\s*\+\s*/)
        .map((part) => part.trim())
        .includes("nakliye")
  );
}

function normalizeText(value) {
  if (value === null || value === undefined || value === false) return "";
  if (typeof value === "object") return "";
  return String(value).trim();
}

function normalizeTimestamp(value) {
  if (value === null || value === undefined || value === false) return "";
  const text = String(value).trim();
  if (!text) return "";
  const num = Number(text);
  return Number.isFinite(num) ? String(num) : text;
}

function normalizePackingIncluded(value) {
  if (value === true || value === false) return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;

  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "true" || normalized === "dahil" || normalized === "included") return true;
  if (normalized === "false" || normalized === "hariç" || normalized === "haric" || normalized === "excluded") {
    return false;
  }

  return null;
}

export function createEmptyTransportPriceEntries() {
  return [
    { packingIncluded: true, currentPrice: "", comparePrice: "" },
    { packingIncluded: false, currentPrice: "", comparePrice: "" },
  ];
}

function normalizeTransportPriceEntry(entry) {
  return {
    packingIncluded: normalizePackingIncluded(entry?.packingIncluded) === true,
    currentPrice: normalizeText(entry?.currentPrice ?? entry?.price),
    comparePrice: normalizeText(
      entry?.comparePrice ??
        entry?.compare_price ??
        entry?.discountPrice ??
        entry?.discount_price
    ),
  };
}

export function normalizeTransportBid(rawBid, request) {
  const bid = rawBid && typeof rawBid === "object" ? rawBid : {};
  const baseEntries = createEmptyTransportPriceEntries();
  const rawPrices = Array.isArray(bid.prices) ? bid.prices.map(normalizeTransportPriceEntry) : [];

  const priceByPacking = new Map();
  rawPrices.forEach((entry) => {
    const key = normalizePackingIncluded(entry?.packingIncluded);
    if (key === true || key === false) {
      priceByPacking.set(String(key), {
        packingIncluded: key,
        currentPrice: normalizeText(entry?.currentPrice),
        comparePrice: normalizeText(entry?.comparePrice),
      });
    }
  });

  return {
    ...bid,
    serviceDayCount: normalizeText(bid.serviceDayCount),
    teamCount: normalizeText(bid.teamCount),
    note: normalizeText(bid.note),
    commissionIncluded: bid.commissionIncluded === true,
    estimatedM3: normalizeText(bid.estimatedM3),
    submittedAt: normalizeTimestamp(bid.submittedAt),
    updatedAt: normalizeTimestamp(bid.updatedAt),
    prices: baseEntries.map((entry) => {
      const key = String(entry.packingIncluded);
      return priceByPacking.get(key) || { ...entry };
    }),
  };
}

export function hasTransportBidContent(rawBid) {
  const bid = normalizeTransportBid(rawBid);
  return Boolean(
    bid.serviceDayCount ||
      bid.teamCount ||
      bid.note ||
      bid.estimatedM3 ||
      bid.prices.some(
        (entry) =>
          String(entry?.currentPrice || "").trim() || String(entry?.comparePrice || "").trim()
      )
  );
}

function getCompanyID(value) {
  return normalizeText(
    value?.companyID ??
      value?.companyId ??
      value?.company_id ??
      value?.corporationID ??
      value?.corporationId ??
      value?.corporation_id ??
      value?.id
  );
}

export function createCompanyMap(companyList = []) {
  const map = new Map();
  (Array.isArray(companyList) ? companyList : []).forEach((company) => {
    const companyID = getCompanyID(company);
    if (!companyID) return;
    map.set(companyID, company);
  });
  return map;
}

function normalizeRevisionRequest(value) {
  if (!value || typeof value !== "object") return null;
  const requestedAt = normalizeTimestamp(value.requestedAt);
  if (!requestedAt) return null;
  return {
    requestedAt,
    requestedByUserID: normalizeText(value.requestedByUserID),
    note: normalizeText(value.note),
    revisionNumber: Number(value.revisionNumber) || 1,
  };
}

function normalizeBidHistoryEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  return {
    bid: normalizeTransportBid(entry.bid),
    // ?? yalnız null/undefined'ı yakalar; Number("abc") = NaN olduğu için etiketlerde
    // "NaN. Revize Teklif" çıkıyordu.
    revisionNumber: Number(entry.revisionNumber) || 0,
    archivedAt: normalizeTimestamp(entry.archivedAt),
  };
}

export function normalizeTransportBidInvitation(rawInvitation, companyById = new Map()) {
  const invitation = rawInvitation && typeof rawInvitation === "object" ? rawInvitation : {};
  const companyID = getCompanyID(invitation) || normalizeText(invitation.companyID);
  const companyFromList = companyById.get(companyID);

  return {
    ...invitation,
    companyID,
    companyTitle:
      normalizeText(invitation.companyTitle) ||
      normalizeText(invitation.title) ||
      normalizeText(companyFromList?.title),
    companyTypes:
      normalizeCompanyTypes(invitation.companyTypes).length > 0
        ? normalizeCompanyTypes(invitation.companyTypes)
        : normalizeCompanyTypes(companyFromList?.type),
    requestedAt: normalizeTimestamp(invitation.requestedAt || invitation.createdDateTime),
    requestedByUserID: normalizeText(invitation.requestedByUserID),
    bid: invitation.bid && typeof invitation.bid === "object" ? normalizeTransportBid(invitation.bid) : null,
    revisionRequest: normalizeRevisionRequest(invitation.revisionRequest),
    bidHistory: Array.isArray(invitation.bidHistory)
      ? invitation.bidHistory.map(normalizeBidHistoryEntry).filter(Boolean)
      : [],
  };
}

export function getTransportBidInvitations(request, companyList = []) {
  const flow =
    request?.transportBidFlow && typeof request.transportBidFlow === "object"
      ? request.transportBidFlow
      : {};
  const invitations = Array.isArray(flow.invitations) ? flow.invitations : [];
  const companyById = createCompanyMap(companyList);

  return invitations
    .map((invitation) => normalizeTransportBidInvitation(invitation, companyById))
    .filter((invitation) => invitation.companyID);
}

export function findTransportBidInvitation(request, companyID, companyList = []) {
  const normalizedCompanyID = normalizeText(companyID);
  if (!normalizedCompanyID) return null;
  return (
    getTransportBidInvitations(request, companyList).find(
      (invitation) => invitation.companyID === normalizedCompanyID
    ) || null
  );
}

export function mergeTransportBidInvitations({
  request,
  companyList = [],
  selectedCompanyIDs = [],
  requestedByUserID = "",
}) {
  const normalizedIDs = Array.from(
    new Set(
      (Array.isArray(selectedCompanyIDs) ? selectedCompanyIDs : [])
        .map((item) => normalizeText(item))
        .filter(Boolean)
    )
  );

  const existingInvitations = getTransportBidInvitations(request, companyList);
  const existingById = new Map(
    existingInvitations.map((invitation) => [invitation.companyID, invitation])
  );
  const companyById = createCompanyMap(companyList);
  const nowTs = String(Math.floor(Date.now() / 1000));

  const merged = normalizedIDs.map((companyID) => {
    const existingInvitation = existingById.get(companyID);
    const company = companyById.get(companyID);
    return {
      ...(existingInvitation || {}),
      companyID,
      companyTitle:
        normalizeText(company?.title) || normalizeText(existingInvitation?.companyTitle),
      companyTypes:
        normalizeCompanyTypes(company?.type).length > 0
          ? normalizeCompanyTypes(company?.type)
          : normalizeCompanyTypes(existingInvitation?.companyTypes),
      requestedAt: existingInvitation?.requestedAt || nowTs,
      requestedByUserID:
        normalizeText(existingInvitation?.requestedByUserID) || normalizeText(requestedByUserID),
      bid: existingInvitation?.bid || null,
    };
  });

  // Seçim listesinde HİÇ TEMSİL EDİLEMEYEN davetler korunur: firma listesi yalnız evrak
  // uyumluluğu onaylı firmaları içerdiği için, sonradan onayı düşen bir firmanın daveti ve
  // girdiği teklif kullanıcı hiç dokunmadan siliniyordu. Kullanıcının ekranda görüp
  // seçimden çıkardığı firmalar (companyList'te olanlar) ise normal şekilde düşer.
  const selectedSet = new Set(normalizedIDs);
  const preserved = existingInvitations.filter(
    (invitation) =>
      invitation?.companyID &&
      !selectedSet.has(invitation.companyID) &&
      !companyById.has(invitation.companyID)
  );

  return [...merged, ...preserved];
}

// Seçimden çıkarılan ve İÇİNDE VERİ (teklif / teklif geçmişi / revize talebi) olan davetler.
// Kaydetmeden önce kullanıcıya onaylatmak için kullanılır.
export function findDroppedInvitationsWithContent({
  request,
  companyList = [],
  selectedCompanyIDs = [],
}) {
  const selectedSet = new Set(
    (Array.isArray(selectedCompanyIDs) ? selectedCompanyIDs : [])
      .map((item) => normalizeText(item))
      .filter(Boolean)
  );
  const companyById = createCompanyMap(companyList);

  return getTransportBidInvitations(request, companyList).filter((invitation) => {
    if (!invitation?.companyID) return false;
    if (selectedSet.has(invitation.companyID)) return false;
    if (!companyById.has(invitation.companyID)) return false; // zaten korunuyor
    const hasBid = hasTransportBidContent(invitation?.bid);
    const hasHistory = Array.isArray(invitation?.bidHistory) && invitation.bidHistory.length > 0;
    const hasRevision = Boolean(invitation?.revisionRequest);
    return hasBid || hasHistory || hasRevision;
  });
}

export function buildRequestWithTransportBidInvitations(request, invitations) {
  const baseRequest = request && typeof request === "object" ? request : {};
  const normalizedInvitations = Array.isArray(invitations) ? invitations : [];

  return {
    ...baseRequest,
    transportBidFlow: {
      ...(baseRequest.transportBidFlow && typeof baseRequest.transportBidFlow === "object"
        ? baseRequest.transportBidFlow
        : {}),
      version: 1,
      lastUpdatedAt: String(Math.floor(Date.now() / 1000)),
      invitations: normalizedInvitations,
    },
  };
}

export function upsertTransportBidForCompany(request, companyID, nextBid, companyList = []) {
  const normalizedCompanyID = normalizeText(companyID);
  const existingInvitations = getTransportBidInvitations(request, companyList);
  const nextInvitations = existingInvitations.map((invitation) => {
    if (invitation.companyID !== normalizedCompanyID) return invitation;
    return {
      ...invitation,
      bid: normalizeTransportBid(nextBid, request),
    };
  });
  return buildRequestWithTransportBidInvitations(request, nextInvitations);
}

export function hasActiveRevisionRequest(invitation) {
  return Boolean(invitation?.revisionRequest?.requestedAt);
}

export function requestTransportBidRevision(request, companyID, { note = "", requestedByUserID = "" } = {}, companyList = []) {
  const normalizedCompanyID = normalizeText(companyID);
  const existingInvitations = getTransportBidInvitations(request, companyList);
  const nowTs = String(Math.floor(Date.now() / 1000));

  const nextInvitations = existingInvitations.map((invitation) => {
    if (invitation.companyID !== normalizedCompanyID) return invitation;
    const historyLength = Array.isArray(invitation.bidHistory) ? invitation.bidHistory.length : 0;
    return {
      ...invitation,
      revisionRequest: {
        requestedAt: nowTs,
        requestedByUserID: normalizeText(requestedByUserID),
        note: normalizeText(note),
        revisionNumber: historyLength + 1,
      },
    };
  });

  return buildRequestWithTransportBidInvitations(request, nextInvitations);
}

export function submitTransportBidRevision(request, companyID, nextBid, companyList = []) {
  const normalizedCompanyID = normalizeText(companyID);
  const existingInvitations = getTransportBidInvitations(request, companyList);
  const nowTs = String(Math.floor(Date.now() / 1000));

  const nextInvitations = existingInvitations.map((invitation) => {
    if (invitation.companyID !== normalizedCompanyID) return invitation;
    const currentHistory = Array.isArray(invitation.bidHistory) ? invitation.bidHistory : [];

    const nextHistory =
      invitation.bid && hasTransportBidContent(invitation.bid)
        ? [
            ...currentHistory,
            {
              bid: normalizeTransportBid(invitation.bid),
              revisionNumber: currentHistory.length,
              archivedAt: nowTs,
            },
          ]
        : currentHistory;

    return {
      ...invitation,
      bid: normalizeTransportBid(nextBid, request),
      bidHistory: nextHistory,
      revisionRequest: null,
    };
  });

  return buildRequestWithTransportBidInvitations(request, nextInvitations);
}

export function getTransportBidStatusMeta(invitation) {
  if (!invitation) return { label: "Gönderilmedi", variant: "neutral" };

  if (hasActiveRevisionRequest(invitation)) {
    return { label: "Revize Bekleniyor", variant: "orange" };
  }

  if (invitation?.bid?.updatedAt || invitation?.bid?.submittedAt) {
    const historyLength = Array.isArray(invitation.bidHistory) ? invitation.bidHistory.length : 0;
    if (historyLength > 0) {
      return { label: "Revize Teklif Girildi", variant: "blue" };
    }
    return { label: "Teklif Girildi", variant: "blue" };
  }

  return { label: "Bekliyor", variant: "orange" };
}

// Talep KOPYALANIRKEN yalnız teklif akışı değil, kopyaya taşınmaması gereken FİZİKSEL
// depo bağları da temizlenir: aksi halde aynı depo odasına ikinci bir çıkış talebi
// bağlanıyor ve Depo Bilgilerim'de odanın "kesin bağı" yanlış talebi gösteriyordu.
export function stripTransportBidFlow(request) {
  if (!request || typeof request !== "object") return request;
  const {
    transportBidFlow,
    isWarehouseExit,
    sourceStorageOperationID,
    sourceStorageOperationId,
    source_storage_operation_id,
    sourceRoomNo,
    source_room_no,
    sourceSnapshotID,
    sourceSnapshotId,
    source_snapshot_id,
    ...rest
  } = request;
  return rest;
}
